import { useEffect, useMemo, useState } from 'react';
import { AI_TRAINING_CRAWLERS, SEO_PATTERN_TOKENS, fillPattern, framedTitle, type SeoConfigSetting, type SeoPatternKind } from '@ecom/contracts';
import { BilingualField, LinesField, TextField, ToggleField, type BilingualValue } from '@/components/fields';
import { CopyIcon } from '@/components/icons';
import { AnnotatedSection, SaveBar } from '@/components/page';
import { Alert, Button, Card, Skeleton } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { storefrontOrigin } from '@/lib/storefront-url';
import { MediaField } from '@/pages/home-layout/fields';
import { OwnerOnly, useSettings } from '@/pages/settings/schema';
import { SettingsPage } from '@/pages/settings';
import { settingValue } from '@/stores/settings.store';
import { useSeoConfigStore } from '@/stores/seo-config.store';
import { useWorkspaceStore } from '@/stores/workspace.store';

const KEY = 'seo.config';
const EMPTY_PAIR: BilingualValue = { ar: '', en: '' };
const KINDS: SeoPatternKind[] = ['product', 'category', 'brand'];
const ENGINES = ['google', 'bing', 'yandex', 'pinterest', 'meta'] as const;

/** The settings as the form holds them: every field present, whatever an older store saved. */
function draftOf(value: unknown): SeoConfigSetting {
  const stored = (value ?? {}) as Partial<SeoConfigSetting>;
  const pair = (candidate: unknown): BilingualValue => ({ ...EMPTY_PAIR, ...(candidate as BilingualValue | undefined) });
  return {
    titleTemplate: pair(stored.titleTemplate),
    home: {
      title: pair(stored.home?.title),
      description: pair(stored.home?.description),
      imageKey: stored.home?.imageKey ?? null,
      imageUrl: stored.home?.imageUrl ?? null,
    },
    patterns: Object.fromEntries(
      KINDS.map((kind) => [kind, { title: pair(stored.patterns?.[kind]?.title), description: pair(stored.patterns?.[kind]?.description) }]),
    ) as SeoConfigSetting['patterns'],
    verification: { google: '', bing: '', yandex: '', pinterest: '', meta: '', ...stored.verification },
    robots: { disallow: stored.robots?.disallow ?? [], blockAiCrawlers: stored.robots?.blockAiCrawlers ?? false },
  };
}

/** What a pattern's example fills in: a believable item of each kind. */
const SAMPLE: Record<SeoPatternKind, Record<'ar' | 'en', Record<string, string>>> = {
  product: {
    en: { name: 'Travel wallet', category: 'Wallets', brand: 'Athar', price: 'SAR 199' },
    ar: { name: 'محفظة سفر', category: 'المحافظ', brand: 'أثر', price: '١٩٩ ر.س' },
  },
  category: { en: { name: 'Wallets', count: '24' }, ar: { name: 'المحافظ', count: '٢٤' } },
  brand: { en: { name: 'Athar', count: '18' }, ar: { name: 'أثر', count: '١٨' } },
};

/**
 * Settings → SEO (ADR-095): how the store's pages read in a search result —
 * the title pattern, the home page, the fill-in patterns that write what an
 * item has not, the codes search engines ask for, and what robots may crawl.
 */
export function SeoSettingsPage() {
  return (
    <SettingsPage title="settingsSeo" subtitle="settingsSeoHint">
      <OwnerOnly>
        <SeoSettings />
      </OwnerOnly>
    </SettingsPage>
  );
}

function SeoSettings() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { entries, loading, saving, error, save } = useSettings();
  const store = useWorkspaceStore((state) => state.workspace?.storeName ?? '');
  const reloadPreviews = useSeoConfigStore((state) => state.load);
  const stored = useMemo(() => draftOf(settingValue(entries, KEY)), [entries]);
  const [draft, setDraft] = useState<SeoConfigSetting>(stored);
  const [copied, setCopied] = useState(false);

  useEffect(() => setDraft(stored), [stored]);

  if (loading && entries.length === 0) return <Skeleton className="h-96" />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const set = (patch: Partial<SeoConfigSetting>) => setDraft((current) => ({ ...current, ...patch }));
  const sitemap = `${storefrontOrigin() ?? ''}/sitemap.xml`;
  const example = framedTitle(draft.titleTemplate[locale], SAMPLE.product[locale].name, store || 'Athar');

  async function submit() {
    // The picture's URL is for this screen; the key is what is kept.
    const home = { title: draft.home.title, description: draft.home.description, imageKey: draft.home.imageKey };
    const robots = { ...draft.robots, disallow: draft.robots.disallow.map((path) => path.trim()).filter(Boolean) };
    if (await save(KEY, { ...draft, home, robots })) void reloadPreviews(true);
  }

  return (
    <Card className="flex flex-col gap-8 p-5">
      <AnnotatedSection title={t('seoTitlePattern')} description={t('seoTitlePatternHint')}>
        <div className="flex flex-col gap-2">
          <BilingualField
            label={t('seoTitlePattern')}
            value={draft.titleTemplate}
            limit={80}
            placeholders={{ ar: '{title} · {store}', en: '{title} · {store}' }}
            onChange={(titleTemplate) => set({ titleTemplate })}
          />
          <p className="text-xs text-ink-muted">
            {t('seoExample')} <span className="font-medium text-ink">{example}</span>
          </p>
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('seoHome')} description={t('seoHomeHint')}>
        <div className="flex flex-col gap-4">
          <BilingualField label={t('metaTitle')} value={draft.home.title} limit={120} placeholders={{ ar: store, en: store }} onChange={(title) => set({ home: { ...draft.home, title } })} />
          <BilingualField
            label={t('metaDescription')}
            multiline
            rows={3}
            limit={320}
            value={draft.home.description}
            placeholders={{ ar: t('seoHomeDescriptionFallback'), en: t('seoHomeDescriptionFallback') }}
            onChange={(description) => set({ home: { ...draft.home, description } })}
          />
          <MediaField
            kind="image"
            label={t('seoShareImage')}
            hint={t('seoStoreShareHint')}
            portrait={false}
            value={draft.home.imageKey && draft.home.imageUrl ? { key: draft.home.imageKey, url: draft.home.imageUrl } : null}
            onChange={(next) => {
              const picked = next as { key: string; url: string } | null;
              set({ home: { ...draft.home, imageKey: picked?.key ?? null, imageUrl: picked?.url ?? null } });
            }}
          />
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('seoPatterns')} description={t('seoPatternsHint')}>
        <div className="flex flex-col gap-6">
          {KINDS.map((kind) => {
            const pattern = draft.patterns[kind];
            const filled = fillPattern(pattern.title[locale], { ...SAMPLE[kind][locale], store: store || 'Athar' });
            const setPattern = (patch: Partial<typeof pattern>) => set({ patterns: { ...draft.patterns, [kind]: { ...pattern, ...patch } } });
            return (
              <fieldset key={kind} className="flex flex-col gap-3 rounded-card border border-line p-4">
                <legend className="px-1 text-sm font-semibold text-ink">{t(`seoPattern_${kind}`)}</legend>
                <p className="text-xs text-ink-muted">
                  {t('seoTokens')}{' '}
                  {SEO_PATTERN_TOKENS[kind].map((token) => (
                    <code key={token} dir="ltr" className="me-1 rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-[11px] text-ink">
                      {`{${token}}`}
                    </code>
                  ))}
                </p>
                <BilingualField label={t('metaTitle')} value={pattern.title} limit={120} placeholders={{ ar: '{name} | {store}', en: '{name} | {store}' }} onChange={(title) => setPattern({ title })} />
                <BilingualField label={t('metaDescription')} multiline rows={2} limit={320} value={pattern.description} onChange={(description) => setPattern({ description })} />
                {filled ? (
                  <p className="text-xs text-ink-muted">
                    {t('seoExample')} <span className="font-medium text-ink">{filled}</span>
                  </p>
                ) : null}
              </fieldset>
            );
          })}
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('seoVerification')} description={t('seoVerificationHint')}>
        <div className="grid gap-4 sm:grid-cols-2">
          {ENGINES.map((engine) => (
            <TextField
              key={engine}
              label={t(`seoVerify_${engine}`)}
              latin
              value={draft.verification[engine]}
              placeholder={t('seoVerifyPlaceholder')}
              onChange={(code) => set({ verification: { ...draft.verification, [engine]: code.replace(/.*content="([^"]*)".*/, '$1').trim() } })}
            />
          ))}
        </div>
      </AnnotatedSection>

      <AnnotatedSection title={t('seoRobots')} description={t('seoRobotsHint')}>
        <div className="flex flex-col gap-4">
          <LinesField
            id="seo-disallow"
            label={t('seoDisallow')}
            hint={t('seoDisallowHint')}
            value={draft.robots.disallow}
            onChange={(disallow) => set({ robots: { ...draft.robots, disallow } })}
          />
          <ToggleField
            label={t('seoBlockAi')}
            hint={t('seoBlockAiHint', { bots: AI_TRAINING_CRAWLERS.join(', ') })}
            checked={draft.robots.blockAiCrawlers}
            onChange={(blockAiCrawlers) => set({ robots: { ...draft.robots, blockAiCrawlers } })}
          />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-muted">{t('seoSitemap')}</span>
            <code dir="ltr" className="rounded bg-surface-sunken px-2 py-1 font-mono text-xs text-ink">
              {sitemap}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(sitemap);
                setCopied(true);
              }}
            >
              <CopyIcon className="size-4" />
              {copied ? t('copied') : t('copy')}
            </Button>
          </div>
        </div>
      </AnnotatedSection>

      {error ? <Alert>{error}</Alert> : null}
      <SaveBar saving={saving === KEY} dirty={dirty} onSave={() => void submit()} onDiscard={() => setDraft(stored)} />
    </Card>
  );
}
